import {
  type OnChangeFn,
  type RowData,
  type Table,
  type TableFeature,
  type Updater,
  functionalUpdate,
  makeStateUpdater,
} from "@tanstack/react-table";

export interface RowExitingTableState {
  exitingRows: Record<string, boolean>;
}

export interface RowExitingOptions {
  enableRowExiting?: boolean;
  onRowExitingChange?: OnChangeFn<Record<string, boolean>>;
}

export interface RowExitingInstance {
  setExitingRow: (updater: Updater<Record<string, boolean>>) => void;
  getExitingRows: () => Record<string, boolean>;
  isRowExiting: (rowId: string) => boolean;
}

declare module "@tanstack/react-table" {
  interface TableState extends RowExitingTableState {}
  interface TableOptionsResolved<TData extends RowData>
    extends RowExitingOptions {}
  interface Table<TData extends RowData> extends RowExitingInstance {}
  interface Row<TData extends RowData> {
    isExiting?: boolean;
  }
}

export const RowExitingFeature: TableFeature<any> = {
  getInitialState: (state): RowExitingTableState => {
    return {
      exitingRows: {},
      ...state,
    };
  },
  getDefaultOptions: <TData extends RowData>(
    table: Table<TData>,
  ): RowExitingOptions => {
    return {
      enableRowExiting: true,
      onRowExitingChange: makeStateUpdater("exitingRows", table),
    } as RowExitingOptions;
  },
  createTable: <TData extends RowData>(table: Table<TData>): void => {
    table.setExitingRow = (updater) => {
      const safeUpdater: Updater<Record<string, boolean>> = (old) => {
        const newState = functionalUpdate(updater, old);
        return newState;
      };
      return table.options.onRowExitingChange?.(safeUpdater);
    };
    table.getExitingRows = () => {
      return table.getState().exitingRows;
    };
    table.isRowExiting = (rowId: string) => {
      return table.getState().exitingRows[rowId] ?? false;
    };
  },
  createRow: <TData extends RowData>(row: any, table: Table<TData>): void => {
    row.isExiting = table.isRowExiting(row.id);
  },
};
